import { PaymentPaymentAllocationInterface, PaymentInterface } from '@vendasta/billing';
import { Currency, currencyFromApi } from '../pricing';
import { PaymentSource, paymentSourceFromApi } from './payment-source';
import { PaymentStatus, paymentStatusFromApi } from './payment-status';
import { PaymentAllocationType, paymentAllocationTypeFromApi } from './payment-allocation-type';
import { PaymentFacilitatorType } from './payment-facilitator-type';

export interface Intent {
  stripeClientSecret?: string;
  intentId?: string;
}

export interface ListPaymentsFilter {
  merchantId?: string;
  allocationIds?: string[];
  start?: Date;
  end?: Date;
}

export interface Payment {
  merchantId: string;
  created?: Date;
  currency: Currency;
  paymentSource: PaymentSource;
  allocations: PaymentAllocation[];
  total: number;
  description: string;
  externalReferenceId: string;
  status: PaymentStatus;
}

export interface PaymentAllocation {
  referenceId: string;
  amount: number;
  type: PaymentAllocationType;
}

export function paymentFromApi(pApi: PaymentInterface): Payment {
  return {
    merchantId: pApi.merchantId || '',
    created: pApi.created ? new Date(pApi.created) : undefined,
    currency: pApi.currency ? currencyFromApi(pApi.currency) : Currency.USD,
    paymentSource: paymentSourceFromApi(pApi.paymentSource),
    allocations: pApi.allocations ? pApi.allocations.map((p) => paymentAllocationFromApi(p)) : [],
    total: pApi.total || 0,
    description: pApi.description || '',
    externalReferenceId: pApi.externalReferenceId || '',
    status: paymentStatusFromApi(pApi.status),
  };
}

export function paymentAllocationFromApi(pApi: PaymentPaymentAllocationInterface): PaymentAllocation {
  return {
    referenceId: pApi.referenceId || '',
    amount: pApi.amount || 0,
    type: pApi.type ? paymentAllocationTypeFromApi(pApi.type) : PaymentAllocationType.UNSET,
  };
}

export interface RetailProvider {
  statementDescriptor: string;
  stripeConnectId: string;
  paymentFacilitatorType: PaymentFacilitatorType;
  countryCode: string;
}

export interface DeclineReason {
  description: string;
  nextSteps: string;
}

export const DeclineReasons: Map<string, DeclineReason> = new Map<string, DeclineReason>([
  [
    'authentication_required',
    {
      description: 'PAYMENT_DECLINE_REASON.AUTHENTICATION_REQUIRED.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.AUTHENTICATION_REQUIRED.NEXT_STEPS',
    },
  ],
  [
    'approve_with_id',
    {
      description: 'PAYMENT_DECLINE_REASON.APPROVE_WITH_ID.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.APPROVE_WITH_ID.NEXT_STEPS',
    },
  ],
  [
    'call_issuer',
    {
      description: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.NEXT_STEPS',
    },
  ],
  [
    'card_not_supported',
    {
      description: 'PAYMENT.DECRINE_REASON.CARD_NOT_SUPPORTED.DESCRIPTION',
      nextSteps: 'PAYMENT.DECRINE_REASON.CARD_NOT_SUPPORTED.NEXT_STEPS',
    },
  ],
  [
    'do_not_honor',
    {
      description: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.NEXT_STEPS',
    },
  ],
  [
    'do_not_try_again',
    {
      description: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.NEXT_STEPS',
    },
  ],
  [
    'duplicate_transaction',
    {
      description: 'PAYMENT_DECLINE_REASON.DUPLICATE_TRANSACTION.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.DUPLICATE_TRANSACTION.NEXT_STEPS',
    },
  ],
  [
    'expired_card',
    {
      description: 'PAYMENT_DECLINE_REASON.EXPIRED_CARD.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.EXPIRED_CARD.NEXT_STEPS',
    },
  ],
  [
    'fraudulent',
    {
      description: 'PAYMENT_DECLINE_REASON.FRAUDULENT.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.FRAUDULENT.NEXT_STEPS',
    },
  ],
  [
    'generic_decline',
    {
      description: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.NEXT_STEPS',
    },
  ],
  [
    'incorrect_number',
    {
      description: 'PAYMENT_DECLINE_REASON.INCORRECT_NUMBER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.INCORRECT_NUMBER.NEXT_STEPS',
    },
  ],
  [
    'incorrect_cvc',
    {
      description: 'PAYMENT_DECLINE_REASON.INCORRECT_CVC.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.INCORRECT_CVC.NEXT_STEPS',
    },
  ],
  [
    'incorrect_pin',
    {
      description: 'PAYMENT_DECLINE_REASON.INCORRECT_PIN.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.INCORRECT_PIN.NEXT_STEPS',
    },
  ],
  [
    'incorrect_zip',
    {
      description: 'PAYMENT_DECLINE_REASON.INCORRECT_ZIP.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.INCORRECT_ZIP.NEXT_STEPS',
    },
  ],
  [
    'insufficient_funds',
    {
      description: 'PAYMENT_DECLINE_REASON.INSUFFICIENT_FUNDS.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.INSUFFICIENT_FUNDS.NEXT_STEPS',
    },
  ],
  [
    'invalid_account',
    {
      description: 'PAYMENT_DECLINE_REASON.INVALID_ACCOUNT.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.INVALID_ACCOUNT.NEXT_STEPS',
    },
  ],
  [
    'invalid_amount',
    {
      description: 'PAYMENT_DECLINE_REASON.INVALID_AMOUNT.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.INVALID_AMOUNT.NEXT_STEPS',
    },
  ],
  [
    'invalid_cvc',
    {
      description: 'PAYMENT_DECLINE_REASON.INVALID_CVC.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.INVALID_CVC.NEXT_STEPS',
    },
  ],
  [
    'invalid_expiry_year',
    {
      description: 'PAYMENT_DECLINE_REASON.INVALID_EXPIRY_YEAR.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.INVALID_EXPIRY_YEAR.NEXT_STEPS',
    },
  ],
  [
    'invalid_number',
    {
      description: 'PAYMENT_DECLINE_REASON.INVALID_NUMBER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.INVALID_NUMBER.NEXT_STEPS',
    },
  ],
  [
    'invalid_pin',
    {
      description: 'PAYMENT_DECLINE_REASON.INVALID_PIN.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.INVALID_PIN.NEXT_STEPS',
    },
  ],
  [
    'issuer_not_available',
    {
      description: 'PAYMENT_DECLINE_REASON.ISSUER_NOT_AVAILABLE.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.ISSUER_NOT_AVAILABLE.NEXT_STEPS',
    },
  ],
  [
    'lost_card',
    {
      description: 'PAYMENT_DECLINE_REASON.LOST_CARD.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.LOST_CARD.NEXT_STEPS',
    },
  ],
  [
    'merchant_blacklist',
    {
      description: 'PAYMENT_DECLINE_REASON.MERCHANT_BLACKLIST.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.MERCHANT_BLACKLIST.NEXT_STEPS',
    },
  ],
  [
    'new_account_information_available',
    {
      description: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.NEXT_STEPS',
    },
  ],
  [
    'no_action_taken',
    {
      description: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.NEXT_STEPS',
    },
  ],
  [
    'not_permitted',
    {
      description: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.NEXT_STEPS',
    },
  ],
  [
    'offline_pin_required',
    {
      description: 'PAYMENT_DECLINE_REASON.OFFLINE_PIN_REQUIRED.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.OFFLINE_PIN_REQUIRED.NEXT_STEPS',
    },
  ],
  [
    'online_or_offline_pin_required',
    {
      description: 'PAYMENT_DECLINE_REASON.ONLINE_OR_OFFLINE_PIN_REQUIRED.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.ONLINE_OR_OFFLINE_PIN_REQUIRED.NEXT_STEPS',
    },
  ],
  [
    'pickup_card',
    {
      description: 'PAYMENT_DECLINE_REASON.PICKUP_CARD.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.PICKUP_CARD.NEXT_STEPS',
    },
  ],
  [
    'pin_try_exceeded',
    {
      description: 'PAYMENT_DECLINE_REASON.PIN_TRY_EXCEEDED.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.PIN_TRY_EXCEEDED.NEXT_STEPS',
    },
  ],
  [
    'processing_error',
    {
      description: 'PAYMENT_DECLINE_REASON.PROCESSING_ERROR.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.PROCESSING_ERROR.NEXT_STEPS',
    },
  ],
  [
    'reenter_transaction',
    {
      description: 'PAYMENT_DECLINE_REASON.REENTER_TRANSACTION.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.REENTER_TRANSACTION.NEXT_STEPS',
    },
  ],
  [
    'restricted_card',
    {
      description: 'PAYMENT_DECLINE_REASON.RESTRICTED_CARD.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.RESTRICTED_CARD.NEXT_STEPS',
    },
  ],
  [
    'revocation_of_all_authorizations',
    {
      description: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.NEXT_STEPS',
    },
  ],
  [
    'revocation_of_authorization',
    {
      description: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.NEXT_STEPS',
    },
  ],
  [
    'security_violation',
    {
      description: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.NEXT_STEPS',
    },
  ],
  [
    'service_not_allowed',
    {
      description: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.NEXT_STEPS',
    },
  ],
  [
    'stolen_card',
    {
      description: 'PAYMENT_DECLINE_REASON.STOLEN_CARD.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.STOLEN_CARD.NEXT_STEPS',
    },
  ],
  [
    'stop_payment_order',
    {
      description: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.NEXT_STEPS',
    },
  ],
  [
    'testmode_decline',
    {
      description: 'PAYMENT_DECLINE_REASON.TESTMODE_DECLINE.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.TESTMODE_DECLINE.NEXT_STEPS',
    },
  ],
  [
    'transaction_not_allowed',
    {
      description: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.UNKNOWN_CONTACT_ISSUER.NEXT_STEPS',
    },
  ],
  [
    'try_again_later',
    {
      description: 'PAYMENT_DECLINE_REASON.TRY_AGAIN_LATER.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.TRY_AGAIN_LATER.NEXT_STEPS',
    },
  ],
  [
    'withdrawal_count_limit_exceeded',
    {
      description: 'PAYMENT_DECLINE_REASON.WITHDRAWAL_COUNT_LIMIT_EXCEEDED.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.WITHDRAWAL_COUNT_LIMIT_EXCEEDED.NEXT_STEPS',
    },
  ],
  [
    'card_velocity_exceeded',
    {
      description: 'PAYMENT_DECLINE_REASON.CARD_VELOCITY_EXCEEDED.DESCRIPTION',
      nextSteps: 'PAYMENT_DECLINE_REASON.CARD_VELOCITY_EXCEEDED.NEXT_STEPS',
    },
  ],
]);
